import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Granularity, Origin } from '../api/types';
import { defaultTimeRange, type TimeRange } from '../components/TimeRangeSelector';

interface FiltersValue {
  presetId: string;
  range: TimeRange;
  origin: Origin;
  granularity: Granularity;
  setRange: (range: TimeRange, presetId: string) => void;
  setOrigin: (origin: Origin) => void;
  setGranularity: (granularity: Granularity) => void;
}

const FiltersContext = createContext<FiltersValue | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }): JSX.Element {
  const initial = defaultTimeRange();
  const [presetId, setPresetId] = useState<string>(initial.presetId);
  const [range, setRangeState] = useState<TimeRange>(initial.range);
  const [origin, setOrigin] = useState<Origin>('all');
  const [granularity, setGranularity] = useState<Granularity>('day');

  const value = useMemo<FiltersValue>(
    () => ({
      presetId,
      range,
      origin,
      granularity,
      setRange: (nextRange, nextPresetId) => {
        setRangeState(nextRange);
        setPresetId(nextPresetId);
      },
      setOrigin,
      setGranularity,
    }),
    [presetId, range, origin, granularity],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersValue {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error('useFilters must be used inside <FiltersProvider>');
  return ctx;
}
